import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  getExamTaskDetails,
  claimExamTask,
  completeExamTask,
  releaseExamTask,
  updateTaskTypeSubject,
  discardExamTask,
  downloadExamTaskFileFromGCS
} from "../../apis/apis";
import { useAuth } from "../../context/AuthContext";
import FileInput from "../../components/form/input/FileInput"; // Importar FileInput
import Button from "../../components/ui/button/Button"; // Importar Button
import Modal from "../../components/ui/modal/Modal"; // Importar un componente de modal
import Select from "../../components/form/select/Select"; // Importar un componente de selección

export default function ExamTaskDetail() {
  const { taskID } = useParams<{ taskID: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [taskDetails, setTaskDetails] = useState<{
    task_id: string;
    name: string;
    description: string;
    state: string;
    subject_name: string;
    subject_id: number;
    user_id: string;
    university_name: string;
    degree_name: string;
    university_id: number; // Agregar university_id
    degree_id: number; // Agregar degree_id
    exam: string;
    eval_type?: string; 
    degree_code?: string; 
    subject_code?: string; 
    type_subject?: string; 
    current_exams?: string[]; // Agregar current_exams

  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<File[]>([]);
  const [selectedExams, setSelectedExams] = useState<File[]>([]);
  const [alert, setAlert] = useState<{
    variant: "success" | "error" | "warning" | "info";
    title: string;
    message: string;
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [evalType, setEvalType] = useState<string>("");
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<"complete" | "completeWithoutExams" | null>(null);
  const [actionLoading, setActionLoading] = useState(false); // Nuevo estado para loading de acciones
  const [typeSubjectLoading, setTypeSubjectLoading] = useState(false); // Nuevo estado para loading de type_subject
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState<string | null>(null);

  useEffect(() => {
    const loadTaskDetails = async () => {
      try {
        setLoading(true);
        if (taskID) {
          const details = await getExamTaskDetails(taskID);
          setTaskDetails(details);
        } else {
          setError("Task ID is missing.");
        }
      } catch (err) {
        console.error("Error fetching task details:", err);
        setError("Failed to load task details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadTaskDetails();
  }, [taskID]);

  const showAlert = (message: string) => {
    window.alert(message); // Utilizar alert normal
  };


  const handleReleaseTask = async () => {
    if (taskDetails) {
      try {
        setActionLoading(true);
        await releaseExamTask(taskDetails.task_id);
        showAlert("Tarea liberada correctamente.");
        navigate("/exam-tasks");
      } catch (error) {
        console.error("Error releasing task:", error);
        showAlert("Error al liberar la tarea.");
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleClaimTask = async () => {
    if (taskDetails) {
      try {
        setActionLoading(true);
        await claimExamTask(taskDetails.task_id);
        showAlert("Tarea reclamada correctamente.");
        setTaskDetails((prevDetails) =>
          prevDetails
            ? { ...prevDetails, state: "WORKING", user_id: user?.id || "" }
            : prevDetails
        ); // Actualizar el estado de la tarea a WORKING y asignar el user_id
      } catch (error) {
        console.error("Error claiming task:", error);
        showAlert("Error al reclamar la tarea.");
      } finally {
        setActionLoading(false);
      }
    }
  };


  const handleCompleteTask = async () => {

    if (taskDetails) {
      // Solo aquí: Si eval_type es "undefined", mostrar el modal para seleccionar tipo de examen
      if (!taskDetails.eval_type || taskDetails.eval_type === "undefined") {
        setIsModalOpen(true);
        setConfirmationAction("complete"); // Asegura que la acción sea "complete"
        return;
      }

      setConfirmationAction("complete");
      setIsConfirmationModalOpen(true);
    }
  };


  const handleSubmitEvalType = async () => {
    if (!evalType) {
      showAlert("Por favor selecciona un tipo de examen.");
      return;
    }

    try {
      setActionLoading(true);
      // Si la acción pendiente es completar sin exámenes

      await completeExamTask(taskDetails?.task_id || "", evalType);
      setIsModalOpen(false);
      setIsConfirmationModalOpen(false);
      setConfirmationAction(null);
      navigate("/exam-tasks");
    } catch (error) {
      console.error("Error completing task:", error);
      showAlert("Error al completar la tarea.");
    } finally {
      setActionLoading(false);
    }
  };

  const confirmAction = async () => {
    if (confirmationAction === "complete" && taskDetails) {
      try {
        setActionLoading(true);
        await completeExamTask(taskDetails.task_id, "");
        navigate("/exam-tasks");
      } catch (error) {
        console.error("Error completing task:", error);
        showAlert("Error al completar la tarea.");
      } finally {
        setActionLoading(false);
      }
    } 

    setIsConfirmationModalOpen(false);
    setConfirmationAction(null);
  };

  const handleTypeSubjectChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    if (!taskDetails) return;
    try {
      setTypeSubjectLoading(true);
      await updateTaskTypeSubject(
        taskDetails.university_id,
        taskDetails.degree_id,
        taskDetails.subject_id,
        newType
      );
      setTaskDetails(prev =>
        prev ? { ...prev, type_subject: newType } : prev
      );
      showAlert("Tipo de asignatura actualizado correctamente.");
    } catch (error) {
      console.error("Error actualizando tipo de asignatura:", error);
      showAlert("Error al actualizar el tipo de asignatura.");
    } finally {
      setTypeSubjectLoading(false);
    }
  };

  const handleDiscardTask = async () => {
    if (taskDetails) {
      if (!window.confirm("¿Estás seguro de que deseas descartar esta tarea? Esta acción no se puede deshacer.")) return;
      try {
        setActionLoading(true);
        await discardExamTask(taskDetails.task_id);
        showAlert("Tarea descartada correctamente.");
        navigate("/exam-tasks");
      } catch (error) {
        console.error("Error discarding task:", error);
        showAlert("Error al descartar la tarea.");
      } finally {
        setActionLoading(false);
      }
    }
  };

  const renderButtons = () => {
    if (!taskDetails) return null;

    const buttons = [];

    if (taskDetails.state.toUpperCase() === "OPEN") {
      buttons.push(
        <Button
          key="claim"
          variant="primary"
          onClick={handleClaimTask}
        >
          Reclamar
        </Button>
      );
    }

    if (taskDetails.state.toUpperCase() === "WORKING" && user?.id === taskDetails.user_id) {
      buttons.push(
        <div key="working-buttons" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-2 flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReleaseTask}
            >
              Liberar
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCompleteTask}
            >
              Validar Examen
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscardTask}
              className="bg-red-600 hover:bg-red-700 text-white border-none"
            >
              Descartar
            </Button>
          </div>
        </div>
      );
    }

    return <div className="flex flex-col gap-4">{buttons}</div>;
  };

  // Extrae la ruta relativa de GCS a partir de la URL pública (sin el bucket)
  const extractGCSPath = (url: string): string | null => {
    // Busca la primera aparición de /UNED/ o similar, después del dominio y el bucket
    // Ejemplo: https://storage.googleapis.com/aitcher-int-bucket/UNED/7002/70021067/docs/E700120970-24J2.pdf
    // Debe devolver: UNED/7002/70021067/docs/E700120970-24J2.pdf
    const match = url.match(/https?:\/\/[^\/]+\/[^\/]+\/(.+)/);
    const result = match ? decodeURIComponent(match[1]) : null;
    console.log("extractGCSPath input:", url, "output:", result);
    return result;
  };

  // Handler para visualizar un examen
  const handleViewExam = async (examUrl: string) => {
    console.log("handleViewExam called with:", examUrl);
    setViewerError(null);
    setViewerLoading(true);
    // Libera el objectURL anterior si existe
    if (viewerUrl && viewerUrl.startsWith("blob:")) {
      try { URL.revokeObjectURL(viewerUrl); } catch {}
      setViewerUrl(null);
    }
    try {
      if (!taskDetails) {
        console.log("No taskDetails");
        return;
      }
      const gcsPath = extractGCSPath(examUrl);
      if (!gcsPath) {
        setViewerError("No se pudo extraer la ruta del archivo.");
        setViewerLoading(false);
        console.log("No se pudo extraer la ruta del archivo.");
        return;
      }
      console.log("Descargando desde backend con:", taskDetails.task_id, gcsPath);
      const blob = await downloadExamTaskFileFromGCS(taskDetails.task_id, gcsPath);
      // Detecta el tipo de archivo y fuerza el tipo si es PDF
      let url: string;
      if (
        blob.type === "application/pdf" ||
        gcsPath.toLowerCase().endsWith(".pdf")
      ) {
        // Si el backend no pone el tipo correcto, forzamos el tipo para PDF
        const pdfBlob =
          blob.type === "application/pdf"
            ? blob
            : new Blob([blob], { type: "application/pdf" });
        url = URL.createObjectURL(pdfBlob);
      } else if (
        blob.type.startsWith("image/") ||
        /\.(png|jpg|jpeg|gif|bmp)$/i.test(gcsPath)
      ) {
        url = URL.createObjectURL(blob);
      } else {
        url = URL.createObjectURL(blob);
      }
      setViewerUrl(url);
      console.log("ObjectURL creado:", url, "tipo:", blob.type, "gcsPath:", gcsPath);
    } catch (err) {
      setViewerError("No se pudo cargar el archivo.");
      console.error("Error en handleViewExam:", err);
    } finally {
      setViewerLoading(false);
    }
  };

  const handleCloseViewer = () => {
    if (viewerUrl && viewerUrl.startsWith("blob:")) {
      try { URL.revokeObjectURL(viewerUrl); } catch {}
    }
    setViewerUrl(null);
  };

  const renderDocumentsAndExams = () => {
    if (!taskDetails) return null;

    return (
      <div className="mt-6 flex flex-col md:flex-row gap-6">
        <div className="flex-1">
          {/* Visualización de current_exams */}
          {taskDetails.current_exams && taskDetails.current_exams.length > 0 && (
            <div className="mb-4">
              <h2 className="text-lg font-bold mb-2">Exámenes actuales</h2>
              <ul className="list-disc pl-5">
                {taskDetails.current_exams.map((examUrl, idx) => (
                  <li key={idx} className="flex items-center justify-between">
                    <button
                      type="button"
                      className="text-blue-500 hover:underline text-left"
                      style={{ flex: 1, textAlign: "left" }}
                      onClick={() => handleViewExam(examUrl)}
                    >
                      {examUrl.split("/").pop() || examUrl}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Solo mostrar documentos si el tipo no es Math ni Lang */}
          {taskDetails.exam && (
            <div>
              <h2 className="text-lg font-bold mb-2">Exámenes</h2>
              <button
                type="button"
                className="text-blue-500 hover:underline text-left"
                style={{ flex: 1, textAlign: "left" }}
                onClick={() => handleViewExam(taskDetails.exam)}
              >
                {taskDetails.exam.split("/").pop() || "Examen"}
              </button>
            </div>
          )}
        </div>
        {/* Panel visor a la derecha */}
        <div className="md:w-1/2 w-full">
          {viewerLoading && (
            <div className="p-4 text-blue-600">Cargando archivo...</div>
          )}
          {viewerError && (
            <div className="p-4 text-red-500">{viewerError}</div>
          )}
          {viewerUrl && (
            <div className="border rounded shadow p-2 bg-gray-50">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">Visor de archivo</span>
                <button
                  className="text-red-500 hover:text-red-700"
                  onClick={handleCloseViewer}
                >
                  Cerrar
                </button>
              </div>
              <iframe
                src={viewerUrl}
                title="Exam Viewer"
                className="w-full"
                style={{ minHeight: "60vh", border: "none" }}
                allow="autoplay"
              />
              <div className="mt-2 text-center">
                <a
                  href={viewerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 underline"
                >
                  Abrir en nueva pestaña
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="p-4">Loading task details...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-500">{error}</div>;
  }

  if (!taskDetails) {
    return <div className="p-4">No task details available.</div>;
  }

  return (
    <div className="p-4 bg-white shadow rounded-lg relative">
      {/* Overlay de loading para acciones */}
      {actionLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-50">
          <div className="flex flex-col items-center">
            <div className="loader border-4 border-blue-500 border-t-transparent rounded-full w-10 h-10 animate-spin mb-2"></div>
            <span className="text-blue-700 font-semibold">Procesando...</span>
          </div>
        </div>
      )}
      {/* Overlay de loading para type_subject */}
      {typeSubjectLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-50">
          <div className="flex flex-col items-center">
            <div className="loader border-4 border-blue-500 border-t-transparent rounded-full w-10 h-10 animate-spin mb-2"></div>
            <span className="text-blue-700 font-semibold">Actualizando tipo de asignatura...</span>
          </div>
        </div>
      )}
      {/* Modal para seleccionar el tipo de examen */}
      {isModalOpen && (
        <Modal onClose={() => setIsModalOpen(false)} title="Selecciona el tipo de examen">
          <div className="mb-4">
            <Select
              label="Tipo de Examen"
              options={[
                { value: "desarrollo", label: "Desarrollo" },
                { value: "test", label: "Test" },
                { value: "mixto", label: "Mixto" },
              ]}
              value={evalType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEvalType(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSubmitEvalType}>
              Confirmar
            </Button>
          </div>
        </Modal>
      )}
      {/* Modal de confirmación */}
      {isConfirmationModalOpen && (
        <Modal
          title="Confirmación"
          onClose={() => setIsConfirmationModalOpen(false)}
        >
          <p>¿Estás seguro de que deseas completar esta acción?</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsConfirmationModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={confirmAction}>
              Confirmar
            </Button>
          </div>
        </Modal>
      )}
      <div className="mb-4">
        <Link
          to="/exam-tasks"
          className="text-blue-500 hover:underline"
        >
          &larr; Volver
        </Link>
      </div>
      <h1 className="text-xl font-bold mb-4">Task Details</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <strong>Task Name:</strong> {taskDetails.name}
        </div>
        <div>
          <strong>Estado:</strong> {taskDetails.state}
        </div>
        <div>
          <strong>Asignatura:</strong> {taskDetails.subject_name} | {taskDetails.subject_code}
        </div>
        <div>
          <strong>Universidad:</strong> {taskDetails.university_name}
        </div>
        <div>
          <strong>Grado:</strong> {taskDetails.degree_name} | {taskDetails.degree_code}
        </div>
        <div>
          <strong>Tipo de Evaluación:</strong> {taskDetails.eval_type}
        </div>
        <div>
          <strong>Tipo de Asignatura:</strong>{" "}
          {(user?.is_superuser || (taskDetails.user_id && user?.id === taskDetails.user_id)) ? (
            <Select
              label=""
              options={[
                { value: "Docu", label: "Docu" },
                { value: "Math", label: "Math" },
                { value: "Lang", label: "Language" }, // Nueva opción
              ]}
              value={taskDetails.type_subject || ""}
              onChange={handleTypeSubjectChange}
            />
          ) : (
            taskDetails.type_subject
          )}
        </div>
        
      </div>

      {renderDocumentsAndExams()}
      <div className="mt-6">
        {renderButtons()}
      </div>
    </div>
  );
}

// Loader CSS (puedes mover esto a tu CSS global si lo prefieres)
<style>
{`
.loader {
  border-width: 4px;
  border-style: solid;
  border-color: #3b82f6 transparent #3b82f6 #3b82f6;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
`}
</style>
